<template>
  <div class="register-email-form">
    <div class="heading">
        <h3 class="mb-s">{{ $t('title.register') }}</h3>
        <div class="line-height-s">
          <i18n
            path="title.registerSubtitle"
            tag="span"
          >
            <strong slot="name"><strong>Bizflycloud.vn</strong></strong>
          </i18n>
        </div>
    </div>
    <bf-form
      :ref="formName"
      class="mt-xs form-email"
      :model="form"
      :rules="formRules"
      @submit.native.prevent="onSubmit">
      <bf-form-item prop="email" :label="$t('form.emailLabel')">
        <bf-input
          ref="email"
          id="ipt-registerform__email"
          :disabled="isLoading"
          autocomplete="on"
          name="username"
          v-model.trim="form.email"
          @keydown.native="handleComplete"
          class="w-100"
          :placeholder="$t('form.placeholderEmail')"
          :clearable="true"
        >
          <template slot="append">
            <bf-button
              :loading="isLoading"
              id="cta-registerform__submit"
              type="primary"
              @click.native.stop.prevent="onSubmit">
              <bf-icon name="arrow-forward" />
            </bf-button>
          </template>
        </bf-input>
      </bf-form-item>
    </bf-form>

    <google-sign-in />

    <!--begin::Gap-->
    <div class="gap pb-l" />
    <!--end::Gap-->
    <i18n
      class="mb-m"
      path="common.termsOfServiceAndPrivacyPolicy"
      tag="p"
    >
      <bf-link slot="tos">
        <a
          :href="DOCS.TERMS"
          target="_blank"
          @click.prevent="showDocFrame(DOCS?.TERMS, termsOfService)"
        >{{ $t("common.termsOfService") }}</a>
      </bf-link>
      <bf-link slot="policy">
        <a
          :href="DOCS.PRIVACY"
          target="_blank"
          @click.prevent="showDocFrame(DOCS?.PRIVACY, privacyPolicy)"
        >{{ $t("common.privacyPolicy") }}</a>
      </bf-link>
    </i18n>
    <p>
      {{ $t('common.alreadyHaveAnAccount')}}
      <router-link
        :to="this.callbackUrl ? `/login?${this.callbackPrefix}=${this.callbackUrl}` : '/login'"
      >
        <bf-link>{{ $t("form.login") }}</bf-link>
      </router-link>
    </p>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import RULES from '@/config/rules';
import {
  setToken,
  validateEmailLength,
  googleTracking,
  showDocFrame,
} from '@/utils/common';
import { DOCS } from '@/config/urls';
import GoogleSignIn from '@/components/forms/GoogleSignIn.vue';
import { createAccount as apiCreateAccount } from '@/services/register';
import { reCaptcha, hideReCaptcha, showReCaptcha } from '@/utils/reCaptcha';
import Cookies from 'js-cookie';

export default {
  name: 'SignIn',
  components: {
    GoogleSignIn,
  },
  props: {
    email: {
      type: String,
      default: '',
    },
    count: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      apiTempToken: process.env.VUE_APP_API_TEMPORARY_TOKEN_KEY || 'tmp_token',
      cookieTempToken: process.env.VUE_APP_COOKIE_TEMPORARY_TOKEN_KEY || 'tmp_token',
      DOCS,
      requestRecaptcha: '',
      isLoading: false,
      formName: 'SignIn',
      form: {
        email: this?.email ?? '',
      },
    };
  },
  computed: {
    ...mapGetters('auth', [
      'callbackUrl', 'callbackPrefix',
    ]),
    termsOfService() {
      return this.$t('common.termsOfService');
    },
    privacyPolicy() {
      return this.$t('common.privacyPolicy');
    },
    formRules() {
      return {
        email: [
          {
            required: true,
            message: this.$t('form.pleaseInput',
            { name: this.$t('form.email') }),
            trigger: 'change',
          },
          {
            pattern: RULES.PATTERN_EMAIL,
            message: this.$t('form.validator.invalid', { field: 'Email' }),
            trigger: 'change',
          },
          {
            validator: validateEmailLength,
            message: `${this.$t('form.validator.limit', { field: 'Email', min: 1, max: 191 })}. ${this.$t('form.validator.emailLength', { field: 'email', min: 1, max: 64 })}`,
            trigger: 'change',
          },
        ],
      };
    },
  },
  watch: {
    count(nextValue, oldValue) {
      if (nextValue !== oldValue) {
        this.checkToBackStep();
        this.$refs.email?.$el?.querySelector('input').focus();
      }
    },
  },
  mounted() {
    this.checkToBackStep();
    this.form.email = this?.email || '';
    this.$refs.email?.$el?.querySelector('input').focus();
    showReCaptcha();
  },
  destroyed() {
    hideReCaptcha();
  },
  methods: {
    showDocFrame,
    async handleComplete(e) {
      const ele = e.target;
      // Wait Autocomplete in 10 ms
      await new Promise((r) => setTimeout(r, 10));
      const nextEmail = ele.value;
      if (this.form.email !== nextEmail) {
        this.form.email = nextEmail;
      }
      try {
        this.$refs[this.formName].validate();
      } catch (e) {
        // pass
      }
    },
    checkToBackStep() {
      if (!this?.email) {
        this.$emit('goToStep', 'step-1');
      }
    },
    async getCaptcha() {
      const requestRecaptcha = await reCaptcha();
      this.requestRecaptcha = requestRecaptcha;
    },
    async onSubmit() {
      this.$refs[this.formName].validate(async (valid) => {
        if (valid) { this.doConfirmPassword(); }
      });
    },
    async doConfirmPassword() {
      try {
        this.isLoading = true;
        await this.getCaptcha();
        const response = await this.createAccount(this.form);
        // Update retry time
        const retryTime = response?.data?.retry_after;
        if (retryTime) {
          this.$emit('updateRetryTime', retryTime);
        }
        // Set token
        const tempToken = response?.data && response?.data[this.apiTempToken];
        setToken(tempToken, this.cookieTempToken);
        // Show message
        this.$notify({
          title: this.$t('notification.success.title'),
          message: response?.message,
          type: 'success',
          customClass: 'success',
        });
        // Tracking Google Event
        const gaData = {
          cn: 'tracking_enter_email',
          ec: 'Mở trang nhập mật khẩu tạm',
          el: 'Mở trang nhập mật khẩu tạm',
        };
        googleTracking(gaData, this.form?.email);
        // Disable loading
        this.isLoading = false;
        // Moving to confirm password
        this.$emit('goToStep', 'step-2');
      } finally {
        this.isLoading = false;
      }
    },
    async createAccount(payload) {
      const data = {
        email: payload?.email,
      };
      const affiliateCode = Cookies.get('bfc_aff');
      if (affiliateCode && affiliateCode.trim() !== '') {
        data.affiliate_code = affiliateCode;
      }
      const res = await apiCreateAccount(
        data,
        this.requestRecaptcha,
      );
      this.$emit('updateEmail', payload?.email);
      return res;
    },
    onReset() {
      this.$refs[this.formName].resetFields();
    },
  },
};
</script>
